import { OFFER_ENCRYPTION_FIELDS, OFFER_STATE_SIGNATURE } from '@makemydeal/dr-platform-types';
import type { AnyAction } from 'redux';
import { combineReducers } from 'redux';
import * as types from '../types/actionTypes';
import * as selectors from '../selectors';
import { mapAction } from './actionMapper';
import {
    cmdMessageToSalesperson,
    cmdMessageToShopper,
    coBuyer,
    commonConsumerId,
    commonOrgId,
    dealerUser,
    message,
    shopper,
    vehicleFulfillment,
    participantsSPD
} from './customerAndShopper';
import { buildUpdatedIncentivesState } from './incentivesStateUtils';
import { initialState } from './initialState';
import { buildUpdatedTermsState, seedEmptyTermConditionalsFromCatalog, zeroUnpricedAppliedIncentiveRows } from './termsStateUtils';
import { buildUpdatedPenciledInputsState } from './penciledInputsReducer/buildPenciledInputsState';
import { INIT_ACTIONS } from './penciledInputsReducer/constants';
import {
    isGridCashDownCatalogCarryEnabled,
    isConditionalIncentivesTotalCashDealLoadEnabled,
    isKeepCashIncentiveCatalogDetailEnabled,
    isCashIncentiveNameOnCatalogLoadEnabled,
    isAppliedIncentiveDisplayFixesEnabled,
    isGridIncentiveManagementEnabled,
    isProgramIncentivesRestorationEnabled,
    isResponseAppliedIncentiveAmountEnabled,
    isGridRebateIdsConsistencyEnabled,
    isSendVehicleFieldsOnPaymentEnabled,
    isSkipNullCashIncentiveIdsEnabled,
    isSkipNullCashIncentiveIdsEnabledForAction,
    isUpqProgramLossBypassEnabled
} from '../utils/incentives';

import { CALC_ENDPOINTS } from '../constants/state';
import {
    dropDealerExcludedStandardsFromBag,
    freezeProgramRebatesOnGoodProgramQuotes,
    hydrateFrozenRebatesOnLoad,
    rederiveAppliedIncentivesOnTermSettle
} from './penciledInputsReducer/helpers';
import {
    creditApp,
    creditAppId,
    creditScore,
    creditScoreSource,
    creditTiers,
    calculationEndpoints,
    customRateGroup,
    paymentTermsFallback
} from './creditAndFinance';
import { featureToggles, shouldDisplayZipModal, toggles } from './featureManagement';
import {
    applicationName,
    dealXgDetails,
    dealXgId,
    dealXgVersion,
    getOfferNodeIdStatus,
    shiftSessionId,
    source,
    udCorrelationId,
    walletId
} from './integrationAndExternalSystems';
import {
    accepted,
    createdOn,
    deal_ref_id,
    dealLifecycle,
    dealRefId,
    deviceType,
    fsDealRefId,
    globalCustomerId,
    incompleteDealDetails,
    initialized,
    offerId,
    offerSource,
    offerType,
    sessionId,
    settings,
    template,
    vehicleInsurance
} from './offerManagement';
import {
    buildEncryptionPaymentState,
    monthlyPayment,
    monthlyPaymentWithoutProducts,
    payment,
    penciledInputsPassthroughReducer,
    totalFinanced
} from './paymentCalculation';
import { incentivesPassthroughReducer, previousTerms, termsPassthroughReducer } from './termsAndIncentives';
import { normalizeLoadedIncentives, normalizeLoadedPaymentIncentives } from './incentivesStateUtils';
import { cashApplyMethod, isTargetOutOfPocket, leaseAnnualMiles, leaseMileage, vehicle } from './vehicleAndPricing';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

const combinedReducer = combineReducers({
    [OFFER_STATE_SIGNATURE]: getOfferNodeIdStatus,
    [OFFER_ENCRYPTION_FIELDS.PAYMENT]: buildEncryptionPaymentState,
    accepted,
    applicationName,
    calculationEndpoints,
    cashApplyMethod,
    cmdMessageToSalesperson,
    cmdMessageToShopper,
    coBuyer,
    commonConsumerId,
    commonOrgId,
    createdOn,
    creditApp,
    creditAppId,
    creditScore,
    creditScoreSource,
    creditTiers,
    customRateGroup,
    deal_ref_id,
    dealLifecycle,
    dealRefId,
    dealerUser,
    dealXgDetails,
    dealXgId,
    dealXgVersion,
    deviceType,
    featureToggles,
    fsDealRefId,
    globalCustomerId,
    incentives: incentivesPassthroughReducer,
    incompleteDealDetails,
    initialized,
    isTargetOutOfPocket,
    leaseAnnualMiles,
    leaseMileage,
    message,
    monthlyPayment,
    monthlyPaymentWithoutProducts,
    offerId,
    offerSource,
    offerType,
    payment,
    paymentTermsFallback,
    penciledInputs: penciledInputsPassthroughReducer,
    previousTerms,
    sessionId,
    settings,
    shiftSessionId,
    shopper,
    shouldDisplayZipModal,
    source,
    terms: termsPassthroughReducer,
    toggles,
    totalFinanced,
    udCorrelationId,
    vehicle,
    walletId,
    template,
    vehicleFulfillment,
    vehicleInsurance,
    participantsSPD
});

const crossSliceReducer = function (state: any, action: AnyFSA) {
    switch (action.type) {
        case types.MAKE_CURRENT_OFFER: {
            const {
                payload: { offer }
            } = action;
            return {
                ...state,
                ...offer,
                ...(offer?.incentives && {
                    incentives: normalizeLoadedIncentives(offer.incentives, isSkipNullCashIncentiveIdsEnabled(state))
                }),
                ...(offer?.payment && {
                    payment: normalizeLoadedPaymentIncentives(offer.payment, isSkipNullCashIncentiveIdsEnabled(state))
                })
            };
        }
        default: {
            // payment received for the use-case of fetching data without relying in current offerType
            const offerType = action.meta?.originalAction?.meta?.middleware?.offerType || state.offerType;
            const updatedPenciledInputs = buildUpdatedPenciledInputsState(
                state.penciledInputs,
                action,
                offerType,
                state,
                state.settings
            );
            const restorationEnabled = isProgramIncentivesRestorationEnabled(state);
            const conditionalIncentivesTotalCashDealLoadEnabled = isConditionalIncentivesTotalCashDealLoadEnabled(state);
            // DE760840 (AC3): resolved from the penciled inputs produced above, so it reflects the term
            // selected now rather than the one this payment call was requested for. Derived once here and
            // threaded onwards so the terms merge and the re-price cannot disagree. Read directly rather than
            // through getOfferTypeTermId, which indexes both levels unguarded and is relied on elsewhere.
            const currentTermId = (updatedPenciledInputs as any)?.byOfferType?.[offerType]?.term as string | undefined;
            const builtTerms = buildUpdatedTermsState(
                state.terms,
                action,
                offerType,
                updatedPenciledInputs,
                restorationEnabled,
                currentTermId,
                isGridCashDownCatalogCarryEnabled(state),
                isUpqProgramLossBypassEnabled(state) &&
                    action.meta?.requestCalculationEndpoint === CALC_ENDPOINTS.userProgramQuotes,
                isAppliedIncentiveDisplayFixesEnabled(state),
                isResponseAppliedIncentiveAmountEnabled(state),
                isGridIncentiveManagementEnabled(state, offerType),
                isGridRebateIdsConsistencyEnabled(state)
            );
            const updatedIncentives = buildUpdatedIncentivesState(
                state.incentives,
                action,
                offerType,
                restorationEnabled,
                updatedPenciledInputs,
                conditionalIncentivesTotalCashDealLoadEnabled,
                isSkipNullCashIncentiveIdsEnabledForAction(state, action),
                isKeepCashIncentiveCatalogDetailEnabled(state),
                isCashIncentiveNameOnCatalogLoadEnabled(state)
            );
            const seededTerms = restorationEnabled
                ? seedEmptyTermConditionalsFromCatalog(builtTerms, updatedIncentives)
                : builtTerms;
            const updatedTerms = isGridIncentiveManagementEnabled(state, offerType)
                ? zeroUnpricedAppliedIncentiveRows(seededTerms, action, updatedIncentives, updatedPenciledInputs, offerType)
                : seededTerms;

            const newState = {
                ...state,
                ...{
                    terms: updatedTerms,
                    incentives: updatedIncentives,
                    penciledInputs: updatedPenciledInputs,
                    [OFFER_ENCRYPTION_FIELDS.PAYMENT]: buildEncryptionPaymentState(state[OFFER_ENCRYPTION_FIELDS.PAYMENT], action)
                }
            };

            // DE760840 (AC1): re-price the applied incentive for the term the user just selected, from this
            // response's own payload.terms. A no-op for every other action. Deliberately after `newState` is
            // assembled and reading the payload rather than the merged slices: the merge deliberately
            // preserves the previous term's catalog on this path, so the merged slices are the wrong source.
            newState.penciledInputs = rederiveAppliedIncentivesOnTermSettle(
                newState,
                action,
                offerType,
                currentTermId,
                restorationEnabled
            );

            // Freeze the program's rebates on a good, non-grid /program-quotes response; a no-op otherwise.
            // Reads `newState` for the same reason the call above does - the merged terms carry this
            // response's program and catalog.
            newState.penciledInputs = freezeProgramRebatesOnGoodProgramQuotes(newState, action, offerType, restorationEnabled);
            newState.penciledInputs = dropDealerExcludedStandardsFromBag(newState, action, offerType);

            // Seed the frozen set for a deal that loads without one (see hydrateFrozenRebatesOnLoad). A no-op
            // for every other action and for deals that already have frozen rows.
            if (INIT_ACTIONS.has(action.type)) {
                newState.penciledInputs = hydrateFrozenRebatesOnLoad(newState, offerType, restorationEnabled);
            }

            if (action.type === types.APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS && isSendVehicleFieldsOnPaymentEnabled(state)) {
                const { msrp, discountOfMsrp, certifiedUsed } = action.payload.vehicle;
                newState.vehicle = { ...newState.vehicle, msrp, discountOfMsrp, certifiedUsed };
            }

            if (selectors.areTermsValid(updatedTerms)) {
                newState.previousTerms = updatedTerms;
            }
            return newState;
        }
    }
};

export default function rootReducer(state: any, action: AnyAction) {
    // Once we update the BFF endpoint to align
    // with the newest interface we can drop this.
    const mappedAction = mapAction(action);
    const intermediateState = combinedReducer(state, mappedAction);

    return crossSliceReducer(intermediateState, mappedAction);
}

/**
 * Utility for expose the logic performed by the offer reducer.
 *
 * @param initOfferPayload
 * @returns offer state slide
 */
// TODO: US762122 - extract and refactor all code in the below function into reusable functions outside of a redux context
export function mapOfferResponseToOffer(initOfferPayload: any) {
    const syntAction = { type: types.INIT_PENCIL_SUCCESS, payload: initOfferPayload };

    // getting state with mapped files
    const mappedAction = mapAction(syntAction);
    const intermediateState = combinedReducer(initialState as any, mappedAction);
    const mappedOffer = crossSliceReducer(intermediateState, mappedAction);

    // getting offer state missed fields
    syntAction.type = types.INIT_SUCCESS;
    const intermediateOffer = combinedReducer(mappedOffer, syntAction);
    return crossSliceReducer(intermediateOffer, syntAction);
}

export { buildUpdatedPenciledInputsState } from './penciledInputsReducer/buildPenciledInputsState';
// Re-export functions from specialized modules for backward compatibility
export {
    calculationEndpoints,
    creditApp,
    creditAppId,
    creditScore,
    creditScoreSource,
    creditTiers,
    paymentTermsFallback
} from './creditAndFinance';
export {
    cmdMessageToSalesperson,
    cmdMessageToShopper,
    coBuyer,
    commonConsumerId,
    commonOrgId,
    dealerUser,
    message,
    shopper
} from './customerAndShopper';
export { featureToggles, shouldDisplayZipModal, toggles } from './featureManagement';
export {
    applicationName,
    dealXgDetails,
    dealXgId,
    dealXgVersion,
    getOfferNodeIdStatus,
    shiftSessionId,
    source,
    udCorrelationId,
    walletId
} from './integrationAndExternalSystems';
export {
    accepted,
    createdOn,
    deal_ref_id,
    dealLifecycle,
    dealRefId,
    deviceType,
    fsDealRefId,
    globalCustomerId,
    incompleteDealDetails,
    initialized,
    offerId,
    offerSource,
    offerType,
    sessionId,
    settings,
    template,
    vehicleInsurance
} from './offerManagement';
export {
    buildEncryptionPaymentState,
    monthlyPayment,
    monthlyPaymentWithoutProducts,
    payment,
    penciledInputsPassthroughReducer,
    totalFinanced
} from './paymentCalculation';
export { incentivesPassthroughReducer, previousTerms, termsPassthroughReducer } from './termsAndIncentives';
export { cashApplyMethod, isTargetOutOfPocket, leaseAnnualMiles, leaseMileage, vehicle } from './vehicleAndPricing';
